import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button } from '@coursera/cds-core';
import type { ButtonProps, IconProps } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';

import { useGetSubscriptionTiersProductPrices } from 'bundles/coursera-plus-landing-page/contentfulData/providerHOC';
import IdealUpgradeEnrollModal from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModal';
import IndiaPriceTestUpgradeModal from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/IndiaPriceTestUpgradeModal';
import { useCourseraPlusSubscriptionFreeTrials } from 'bundles/coursera-plus/hooks/useCourseraPlusSubscriptionFreeTrials';
import { useEnrollInOrUpgradeToCourseraPlus } from 'bundles/coursera-plus/hooks/useEnrollInOrUpgradeToCourseraPlus';
import { getIsLearnerImpressedInIndiaTiersEpic } from 'bundles/coursera-plus/utils/subscriptionTiersUtils';
import { useExistingCourseraLiteSubscription } from 'bundles/coursera-plus/utils/withExistingCourseraLiteSubscription';
import { MONTHLY } from 'bundles/subscriptions/common/BillingCycleType';

import _t from 'i18n!nls/coursera-plus';

export type ButtonPropsType = ButtonProps['variant'];

type PropsFromCaller = {
  ctaLabel?: string | null;
  ctaVariant?: ButtonPropsType;
  trackingName: string;
  trackEnrollCtaClick?: () => void;
  ctaIcon?: React.ReactElement<IconProps>;
};

const EnrollOrUpgradeToCourseraPlus: React.FC<PropsFromCaller> = ({
  ctaLabel,
  ctaVariant,
  trackingName,
  ctaIcon,
  trackEnrollCtaClick,
}) => {
  const [idealUpgradeModalOpen, setIdealUpgradeModalOpen] = React.useState(false);
  const { existingCourseraLiteSubscription, allSubscriptions } = useExistingCourseraLiteSubscription();
  const isSubscribedToCourseraPlus = allSubscriptions?.some(
    (subscription) =>
      subscription.isCourseraPlusSubscription &&
      (subscription.isActive || subscription.isFreeTrial || subscription.isCancelled)
  );
  const { courseraPlusProductPrice } = useGetSubscriptionTiersProductPrices({
    skip: !existingCourseraLiteSubscription?.id,
    shouldGetCourseraPlusAnnual: false,
  });
  const { userHasCourseraPlusFreeTrial } = useCourseraPlusSubscriptionFreeTrials();
  const retrackedTrack = useRetracked();

  const {
    isSubmitting: isEnrollingInCourseraPlus,
    handleEnrollInCourseraPlus,
    error: cPlusenrollmentError,
  } = useEnrollInOrUpgradeToCourseraPlus();

  const onClose = () => {
    setIdealUpgradeModalOpen(false);
  };

  if (isSubscribedToCourseraPlus) {
    return null;
  }

  if (getIsLearnerImpressedInIndiaTiersEpic()) {
    return <IndiaPriceTestUpgradeModal onClose={onClose} />;
  }

  const handleButtonClick = () => {
    trackEnrollCtaClick?.();

    if (trackingName) {
      retrackedTrack({
        trackingName,
        trackingData: {},
        action: 'click',
      });
    }

    if (Boolean(existingCourseraLiteSubscription?.id)) {
      setIdealUpgradeModalOpen(true);
    } else {
      handleEnrollInCourseraPlus();
    }
  };

  const getButtonIcon = () => {
    if (cPlusenrollmentError) {
      return <ErrorIcon color="error" />;
    } else if (ctaIcon) {
      return ctaIcon;
    }
    return undefined;
  };

  let _ctaLabel = ctaLabel;
  if (existingCourseraLiteSubscription) {
    _ctaLabel = _t('Switch to Coursera Plus');
  } else if (!userHasCourseraPlusFreeTrial) {
    _ctaLabel = _t('Join Coursera Plus');
  }

  return (
    <>
      <Button
        component="button"
        variant={ctaVariant || 'primary'}
        loading={isEnrollingInCourseraPlus}
        icon={getButtonIcon()}
        onClick={handleButtonClick}
      >
        {_ctaLabel}
      </Button>

      {idealUpgradeModalOpen && courseraPlusProductPrice && (
        <IdealUpgradeEnrollModal
          courseraPlusProductPrice={courseraPlusProductPrice}
          onClose={onClose}
          isStandaloneCourseraPlusUpgrade={true}
          toTierBillingCycle={MONTHLY}
        />
      )}
    </>
  );
};

export default EnrollOrUpgradeToCourseraPlus;
